/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    ConfigurationService,
    toDateFromISO8601,
} from '@interacta-shared/data-access-configuration';
import {
    NotificationEvent,
    NotificationEventNavigationData,
} from '@interacta-shared/data-access-notification';

import { isNotDefined } from '@interacta-shared/util';
import {
    IUser,
    IUsersGroup,
    toIUser,
    toIUsersGroup,
} from '@interacta-shared/util-caronte';
import { parseIfNotNull } from '@interacta-shared/util-common';
import { genericWrapMember } from '@interacta-shared/util-member';
import {
    toHashtag,
    toWorkflowOperation,
    toWorkflowStatus,
} from '@interacta/data-access-community';
import {
    toAttachmentDetails,
    toComment,
    toGenericPost,
} from '@interacta/data-access-post';
import { isActivityPostAndNotLeaf } from '@interacta/util-post';
import { CommunityTreeDeserialize } from '@modules/communities/models/communities.model';
import { WorkspaceDeserialize } from '@modules/communities/models/workspace.model';
import { PostDetailSection } from '@modules/core';
import { toILabInterop } from '@modules/i-lab-interop/models/i-lab-interop.model';
import { toGenericTask } from '@modules/tasks/models/task.deserialize';
import {
    NotificationChangedValue,
    NotificationPostDataChangedValues,
    UserNotificationEventDelta,
    UserNotificationEventType,
} from './user-notification.model';

export const toUserNotificationEventDelta = (
    record: any,
    configurationService: ConfigurationService,
): UserNotificationEventDelta & NotificationEventNavigationData => {
    const post = record.post
        ? toGenericPost(configurationService, record.post)
        : null;

    const notification: UserNotificationEventDelta = {
        hashtags: record.hashtags ? record.hashtags.map(toHashtag) : [],
        post,
        postDataChangedValues: parseIfNotNull(
            record.postDataChangedValues?.activity,
            toNotificationPostDataChangedValues,
        ),
        task: record.task
            ? toGenericTask(record.task, configurationService, post)
            : null,
        postWorkflowOperationDetail: parseIfNotNull(
            record.postWorkflowOperationDetail,
            toWorkflowOperation,
        ),
        firstPostWorkflowOperationDetail: parseIfNotNull(
            record.firstPostWorkflowOperationDetail,
            toWorkflowOperation,
        ),
        lastPostWorkflowOperationDetail: parseIfNotNull(
            record.lastPostWorkflowOperationDetail,
            toWorkflowOperation,
        ),
        lastTaskTransitionOperationDetail:
            record.lastTaskTransitionOperationDetail ?? null,
        workflowState: parseIfNotNull(record.workflowState, toWorkflowStatus),
        workspace: parseIfNotNull(
            record.workspace,
            WorkspaceDeserialize.workspaceDetails,
        ),
        addedAttachments: record.addedAttachments
            ? record.addedAttachments.map(toAttachmentDetails)
            : [],
        updatedAttachments: record.updatedAttachments
            ? record.updatedAttachments.map(toAttachmentDetails)
            : [],
        versionedAttachments: record.versionedAttachments
            ? record.versionedAttachments.map(toAttachmentDetails)
            : [],
        community: parseIfNotNull(
            record.community,
            CommunityTreeDeserialize.communityInfoDetails,
        ),
        comment: parseIfNotNull(record.comment, toComment),
        note: parseIfNotNull(record.note, toILabInterop),
    };
    return {
        ...notification,
        ...toUserNotificationEventRouterLink(record.typeId, notification),
    };
};

const toNotificationPostDataChangedValues = (
    record: any,
): NotificationPostDataChangedValues => {
    const assigneeUser: NotificationChangedValue<IUser> | null =
        record.assigneeUser
            ? {
                  oldValue: parseIfNotNull(
                      record.assigneeUser.oldValue,
                      toIUser,
                  ),
                  newValue: parseIfNotNull(
                      record.assigneeUser.newValue,
                      toIUser,
                  ),
              }
            : null;

    const assigneeGroup: NotificationChangedValue<IUsersGroup> | null =
        record.assigneeGroup
            ? {
                  oldValue: parseIfNotNull(
                      record.assigneeGroup.oldValue,
                      toIUsersGroup,
                  ),
                  newValue: parseIfNotNull(
                      record.assigneeGroup.newValue,
                      toIUsersGroup,
                  ),
              }
            : null;

    return {
        assignee:
            assigneeUser || assigneeGroup
                ? {
                      oldValue: assigneeUser?.oldValue
                          ? genericWrapMember(assigneeUser.oldValue)
                          : assigneeGroup?.oldValue
                            ? genericWrapMember(assigneeGroup.oldValue)
                            : null,
                      newValue: assigneeUser?.newValue
                          ? genericWrapMember(assigneeUser.newValue)
                          : assigneeGroup?.newValue
                            ? genericWrapMember(assigneeGroup.newValue)
                            : null,
                  }
                : null,
        expectedStartDate: record.expectedStartDate
            ? {
                  oldValue:
                      toDateFromISO8601(record.expectedStartDate.oldValue) ??
                      null,
                  newValue:
                      toDateFromISO8601(record.expectedStartDate.newValue) ??
                      null,
              }
            : null,
        expectedEndDate: record.expectedEndDate
            ? {
                  oldValue:
                      toDateFromISO8601(record.expectedEndDate.oldValue) ??
                      null,
                  newValue:
                      toDateFromISO8601(record.expectedEndDate.newValue) ??
                      null,
              }
            : null,
        priority:
            record.priority != null
                ? {
                      oldValue: record.priority.oldValue,
                      newValue: record.priority.newValue,
                  }
                : null,
    };
};

const toUserNotificationEventRouterLink = (
    typeId: NotificationEvent['typeId'],
    notification: UserNotificationEventDelta,
): NotificationEventNavigationData => {
    switch (typeId) {
        case UserNotificationEventType.ADD_ATTACHMENT:
        case UserNotificationEventType.REMOVE_ATTACHMENT:
        case UserNotificationEventType.ADD_ATTACHMENT_VERSION:
            return toPostDetailRouterLink(
                notification,
                PostDetailSection.ATTACHMENTS,
            );
        case UserNotificationEventType.COMMENT:
        case UserNotificationEventType.REMOVE_COMMENT:
        case UserNotificationEventType.MENTION_COMMENT:
        case UserNotificationEventType.LIKE_COMMENT:
        case UserNotificationEventType.ADD_COMMENT_ON_YOUR_POST:
        case UserNotificationEventType.REPLY_YOUR_COMMENT:
            return toPostDetailRouterLink(
                notification,
                PostDetailSection.COMMENTS,
            );
        case UserNotificationEventType.CHANGE_STATUS:
        case UserNotificationEventType.EDIT_SCREEN_DATA:
            return toPostDetailRouterLink(
                notification,
                PostDetailSection.WORKFLOW,
            );
        case UserNotificationEventType.CREATE_TASK:
        case UserNotificationEventType.EDIT_TASK:
        case UserNotificationEventType.COPY_TASK:
        case UserNotificationEventType.DELETE_TASK:
        case UserNotificationEventType.TASK_REMINDER:
        case UserNotificationEventType.ADD_COMMENT_ON_TASK:
        case UserNotificationEventType.MENTION_TASK_COMMENT:
        case UserNotificationEventType.DELETE_TASK_COMMENT:
        case UserNotificationEventType.MENTION_TASK:
        case UserNotificationEventType.ASSIGNED_TASK:
        case UserNotificationEventType.ADD_TASK_ATTACHMENT:
        case UserNotificationEventType.REMOVE_TASK_ATTACHMENT:
        case UserNotificationEventType.REOPENED_YOUR_TASK:
        case UserNotificationEventType.CLOSED_YOUR_TASK:
        case UserNotificationEventType.DELETED_YOUR_TASK:
            return toPostDetailRouterLink(
                notification,
                PostDetailSection.TASKS,
            );
        case UserNotificationEventType.EVENT_INVITATION_STAFF:
            return toPostDetailRouterLink(
                notification,
                PostDetailSection.INVITATIONS,
            );
        case UserNotificationEventType.FEEDBACK_TASK_CHANGE_STATUS: {
            const postId = notification.post?.id;
            return postId
                ? {
                      routerLink: ['/post', 'feedback', postId, 'recipient'],
                      queryParams: {
                          taskId: !notification.task?.deleted
                              ? notification.task?.id
                              : undefined,
                      },
                  }
                : {};
        }
        case UserNotificationEventType.REMOVE_POST:
            return {};
        case UserNotificationEventType.SHARE_ILAB_VIEW:
        case UserNotificationEventType.SHARE_ILAB_EDIT:
            return toILabDetailRouterLink(notification);
        default:
            return toPostDetailRouterLink(notification);
    }
};

const toILabDetailRouterLink = (
    notification: UserNotificationEventDelta,
): NotificationEventNavigationData => {
    const id = notification.note?.id;
    if (isNotDefined(id)) {
        throw new Error('missing required iLab id');
    }

    return {
        routerLink: ['/i-lab', id, 'details'],
    };
};

const toPostDetailRouterLink = (
    notification: UserNotificationEventDelta,
    flowType?: PostDetailSection,
): NotificationEventNavigationData => {
    const postId = notification.post?.id;
    if (isNotDefined(postId)) {
        throw new Error('missing required post id');
    }

    if (
        !flowType &&
        notification.post &&
        isActivityPostAndNotLeaf(notification.post)
    ) {
        flowType = PostDetailSection.SUBACTIVITIES;
    }

    const queryParams = flowType
        ? {
              entity: flowType,
              commentId:
                  flowType === PostDetailSection.COMMENTS &&
                  !notification.comment?.deleted &&
                  notification.comment?.id
                      ? notification.comment.id
                      : undefined,
              taskId:
                  flowType === PostDetailSection.TASKS &&
                  !notification.task?.deleted &&
                  notification.task?.id
                      ? notification.task.id
                      : undefined,
          }
        : undefined;

    return {
        routerLink: ['/post', postId],
        queryParams,
    };
};
